/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the file COPYING for more details.
 */

import type { VNode } from "preact";
import { useEffect, useState } from "preact/hooks";
import {
  ERROR_INJECTION_STORAGE_KEY,
  ERROR_INJECTION_EVENTS_CHANNEL,
  clearErrorInjectionConfig,
  emptyErrorInjectionConfig,
  readErrorInjectionConfig,
  writeErrorInjectionConfig,
  type ErrorInjectionAction,
  type ErrorInjectionConfig,
  type ErrorInjectionEvent,
  type ErrorInjectionRule,
} from "../testing/error-injection-http.js";
import { Button } from "../ui/Button.js";
import { Input } from "../ui/Input.js";
import { useTranslation } from "../context/translation.js";

const methods = ["*", "GET", "POST", "PUT", "PATCH", "DELETE"];

function newRule(): ErrorInjectionRule {
  return {
    id: crypto.randomUUID(),
    enabled: false,
    method: "GET",
    url: "/private/",
    action: "status",
    status: 500,
  };
}

function integer(value: string): number | undefined {
  if (value.trim() === "") return undefined;
  const result = Number(value);
  return Number.isFinite(result) && result >= 0
    ? Math.floor(result)
    : undefined;
}

function actionStyle(action: ErrorInjectionEvent["action"]): string {
  switch (action) {
    case "status":
      return "bg-red-100 text-red-800";
    case "hang":
      return "bg-amber-100 text-amber-800";
    case "delayed":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

/** Standalone developer control panel, intentionally not linked from the menu. */
export function ErrorInjectionTestScreen(): VNode {
  const { t } = useTranslation();
  const [config, setConfig] = useState<ErrorInjectionConfig>(() =>
    readErrorInjectionConfig(),
  );
  const [events, setEvents] = useState<ErrorInjectionEvent[]>([]);

  useEffect(() => {
    if (readErrorInjectionConfig().enabled) {
      console.info("[taler-merchant-webui] HTTP error injection is enabled.");
    }
  }, []);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === ERROR_INJECTION_STORAGE_KEY || event.key === null) {
        setConfig(readErrorInjectionConfig());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(ERROR_INJECTION_EVENTS_CHANNEL);
    channel.onmessage = (message: MessageEvent<unknown>) => {
      const event = message.data;
      if (
        !event ||
        typeof event !== "object" ||
        (event as { type?: unknown }).type !== "request"
      )
        return;
      setEvents((previous) =>
        [...previous, event as ErrorInjectionEvent].slice(-100),
      );
    };
    return () => channel.close();
  }, []);

  const save = (next: ErrorInjectionConfig) => {
    setConfig(next);
    writeErrorInjectionConfig(next);
  };
  const updateRule = (id: string, patch: Partial<ErrorInjectionRule>) => {
    save({
      ...config,
      rules: config.rules.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    });
  };

  return (
    <main
      class="max-w-5xl mx-auto p-6 md:p-10 space-y-8 text-gray-900"
      data-error-injection-test
    >
      <header class="space-y-2">
        <h1 class="text-2xl font-bold">{t`HTTP error injection`}</h1>
        <p class="text-sm text-gray-600 max-w-3xl">
          {t`These settings are stored in this browser's local storage. Keep this page open in one tab and use the merchant portal in another: each new API request reads the current settings.`}
        </p>
      </header>

      <div class="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]">
        <div class="space-y-8">
          <section
            class={`border rounded-lg p-5 flex flex-wrap items-center justify-between gap-4 ${config.enabled ? "border-red-300 bg-red-50" : "border-gray-300 bg-gray-50"}`}
          >
            <div>
              <h2 class="font-bold">
                {config.enabled ? t`Error injection is enabled` : t`Error injection is disabled`}
              </h2>
              <p class="text-sm text-gray-600">
                {t`Rules are saved while disabled, but requests pass through unchanged.`}
              </p>
            </div>
            <div class="flex flex-wrap gap-2">
              <Button
                variant={config.enabled ? "danger" : "primary"}
                onClick={() => save({ ...config, enabled: !config.enabled })}
              >
                {config.enabled
                  ? t`Disable error injection`
                  : t`Enable error injection`}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  clearErrorInjectionConfig();
                  setConfig(emptyErrorInjectionConfig());
                }}
              >
                {t`Clear all settings`}
              </Button>
            </div>
          </section>

          <section class="border border-amber-300 bg-amber-50 rounded-lg p-5 space-y-4">
            <h2 class="font-bold">{t`Default behavior for all requests`}</h2>
            <div class="grid gap-4 md:grid-cols-2">
              <label class="space-y-1 text-sm font-semibold">
                <span>{t`Response`}</span>
                <select
                  class="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                  value={
                    config.defaultAction === "status"
                      ? String(config.defaultStatus ?? 500)
                      : (config.defaultAction ?? "pass")
                  }
                  onChange={(event) => {
                    const value = (event.target as HTMLSelectElement).value;
                    save({
                      ...config,
                      ...(value === "pass"
                        ? {}
                        : value === "hang"
                          ? { defaultAction: "hang" }
                          : {
                              defaultAction: "status",
                              defaultStatus: Number(value) as 400 | 500,
                            }),
                      ...(value === "pass"
                        ? { defaultAction: undefined, defaultStatus: undefined }
                        : {}),
                    });
                  }}
                >
                  <option value="pass">{t`Pass through to backend`}</option>
                  <option value="400">{t`Always return HTTP 400`}</option>
                  <option value="500">{t`Always return HTTP 500`}</option>
                  <option value="hang">{t`Never return a response`}</option>
                </select>
              </label>
              <Input
                label={t`Additional response delay (milliseconds)`}
                type="number"
                value={config.delayMs}
                onInput={(event) =>
                  save({
                    ...config,
                    delayMs:
                      integer((event.target as HTMLInputElement).value) ?? 0,
                  })
                }
                helperText={t`Applied to responses which are allowed to return.`}
              />
            </div>
            {config.defaultAction === "status" && (
              <div class="grid gap-4 md:grid-cols-2">
                <label class="space-y-1 text-sm font-semibold">
                  <span>{t`Error response content`}</span>
                  <select
                    class="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                    value={config.defaultContent ?? "taler-error"}
                    onChange={(event) =>
                      save({
                        ...config,
                        defaultContent: (event.target as HTMLSelectElement)
                          .value as ErrorInjectionConfig["defaultContent"],
                      })
                    }
                  >
                    <option value="taler-error">{t`Taler JSON error`}</option>
                    <option value="empty">{t`Empty response body`}</option>
                    <option value="html">text/html</option>
                  </select>
                </label>
                {(config.defaultContent ?? "taler-error") === "taler-error" && (
                  <Input
                    label={t`Taler error code`}
                    type="number"
                    value={config.defaultErrorCode ?? 60}
                    onInput={(event) =>
                      save({
                        ...config,
                        defaultErrorCode:
                          integer((event.target as HTMLInputElement).value) ??
                          60,
                      })
                    }
                    helperText={t`Defaults to GENERIC_INTERNAL_INVARIANT_FAILURE (60).`}
                  />
                )}
              </div>
            )}
            {config.defaultAction === "status" &&
              (config.defaultContent ?? "taler-error") === "html" && (
                <label class="space-y-1 text-sm font-semibold block">
                  <span>{t`HTML response body`}</span>
                  <textarea
                    class="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg bg-white font-mono"
                    rows={4}
                    value={config.defaultHtml ?? "<h1>Error injection</h1>"}
                    onInput={(event) =>
                      save({
                        ...config,
                        defaultHtml: (event.target as HTMLTextAreaElement)
                          .value,
                      })
                    }
                  />
                </label>
              )}
          </section>

          <section class="space-y-4">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 class="font-bold">{t`Request-specific rules`}</h2>
                <p class="text-sm text-gray-600">
                  {t`The first matching rule wins. URL is a case-sensitive substring of the complete request URL.`}
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={() =>
                  save({ ...config, rules: [...config.rules, newRule()] })
                }
              >
                {t`Add rule`}
              </Button>
            </div>

            {config.rules.length === 0 ? (
              <p class="text-sm text-gray-500 border rounded-lg p-4">
                {t`No rules. Add one to affect only selected requests.`}
              </p>
            ) : (
              config.rules.map((rule, index) => (
                <article
                  key={rule.id}
                  class="border rounded-lg p-4 space-y-4 bg-white"
                  data-error-rule={rule.id}
                >
                  <div class="flex justify-between items-center gap-4">
                    <h3 class="font-semibold">
                      {t`Rule ${index + 1}`}
                      {rule.enabled === false ? t` (inactive)` : ""}
                    </h3>
                    <div class="flex gap-2">
                      <Button
                        variant={
                          rule.enabled === false ? "primary" : "secondary"
                        }
                        size="sm"
                        onClick={() =>
                          updateRule(rule.id, {
                            enabled: rule.enabled === false,
                          })
                        }
                      >
                        {rule.enabled === false ? t`Activate` : t`Disable`}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() =>
                          save({
                            ...config,
                            rules: config.rules.filter((r) => r.id !== rule.id),
                          })
                        }
                      >
                        {t`Remove`}
                      </Button>
                    </div>
                  </div>
                  {rule.enabled === false && (
                    <p class="text-sm text-amber-700 bg-amber-50 rounded p-3">
                      {t`This new rule is inactive and cannot affect requests until you activate it.`}
                    </p>
                  )}
                  <div class="grid gap-4 md:grid-cols-4">
                    <label class="space-y-1 text-sm font-semibold">
                      <span>{t`Method`}</span>
                      <select
                        class="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                        value={rule.method}
                        onChange={(event) =>
                          updateRule(rule.id, {
                            method: (event.target as HTMLSelectElement).value,
                          })
                        }
                      >
                        {methods.map((method) => (
                          <option key={method} value={method}>
                            {method}
                          </option>
                        ))}
                      </select>
                    </label>
                    <Input
                      label={t`URL contains`}
                      value={rule.url}
                      onInput={(event) =>
                        updateRule(rule.id, {
                          url: (event.target as HTMLInputElement).value,
                        })
                      }
                    />
                    <label class="space-y-1 text-sm font-semibold">
                      <span>{t`Inject`}</span>
                      <select
                        class="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                        value={rule.action}
                        onChange={(event) =>
                          updateRule(rule.id, {
                            action: (event.target as HTMLSelectElement)
                              .value as ErrorInjectionAction,
                          })
                        }
                      >
                        <option value="status">{t`HTTP error`}</option>
                        <option value="hang">{t`No response`}</option>
                        <option value="delay">{t`Delay real response`}</option>
                      </select>
                    </label>
                    <Input
                      label={t`First N matches (empty = every match)`}
                      type="number"
                      value={rule.remaining ?? ""}
                      onInput={(event) =>
                        updateRule(rule.id, {
                          remaining: integer(
                            (event.target as HTMLInputElement).value,
                          ),
                        })
                      }
                    />
                  </div>
                  {rule.action === "status" && (
                    <div class="grid gap-4 md:grid-cols-3">
                      <label class="space-y-1 text-sm font-semibold block">
                        <span>{t`Status`}</span>
                        <select
                          class="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                          value={rule.status ?? 500}
                          onChange={(event) =>
                            updateRule(rule.id, {
                              status: Number(
                                (event.target as HTMLSelectElement).value,
                              ) as 400 | 500,
                            })
                          }
                        >
                          <option value="400">HTTP 400</option>
                          <option value="500">HTTP 500</option>
                        </select>
                      </label>
                      <label class="space-y-1 text-sm font-semibold block">
                        <span>{t`Error response content`}</span>
                        <select
                          class="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                          value={rule.content ?? "taler-error"}
                          onChange={(event) =>
                            updateRule(rule.id, {
                              content: (event.target as HTMLSelectElement)
                                .value as ErrorInjectionRule["content"],
                            })
                          }
                        >
                          <option value="taler-error">{t`Taler JSON error`}</option>
                          <option value="empty">{t`Empty response body`}</option>
                          <option value="html">text/html</option>
                        </select>
                      </label>
                      {(rule.content ?? "taler-error") === "taler-error" && (
                        <Input
                          label={t`Taler error code`}
                          type="number"
                          value={rule.errorCode ?? 60}
                          onInput={(event) =>
                            updateRule(rule.id, {
                              errorCode:
                                integer(
                                  (event.target as HTMLInputElement).value,
                                ) ?? 60,
                            })
                          }
                        />
                      )}
                    </div>
                  )}
                  {rule.action === "status" &&
                    (rule.content ?? "taler-error") === "html" && (
                      <label class="space-y-1 text-sm font-semibold block">
                        <span>{t`HTML response body`}</span>
                        <textarea
                          class="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-lg bg-white font-mono"
                          rows={4}
                          value={rule.html ?? "<h1>Error injection</h1>"}
                          onInput={(event) =>
                            updateRule(rule.id, {
                              html: (event.target as HTMLTextAreaElement).value,
                            })
                          }
                        />
                      </label>
                    )}
                  {rule.action === "delay" && (
                    <div class="max-w-xs">
                      <Input
                        label={t`Delay (milliseconds)`}
                        type="number"
                        value={rule.delayMs ?? 0}
                        onInput={(event) =>
                          updateRule(rule.id, {
                            delayMs:
                              integer(
                                (event.target as HTMLInputElement).value,
                              ) ?? 0,
                          })
                        }
                      />
                    </div>
                  )}
                </article>
              ))
            )}
          </section>
        </div>

        <section class="space-y-4 min-w-0 self-start rounded-xl border border-gray-200 bg-white p-5 shadow-sm lg:sticky lg:top-6">
          <div class="flex flex-wrap gap-3 items-start justify-between border-b border-gray-100 pb-4">
            <div>
              <h2 class="font-bold">{t`Live request activity`}</h2>
              <p class="text-sm text-gray-600">
                {t`Events arrive from other tabs via BroadcastChannel and disappear when this page is closed.`}
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={events.length === 0}
              onClick={() => setEvents([])}
            >
              {t`Clear`}
            </Button>
          </div>
          {events.length === 0 ? (
            <p class="text-sm text-gray-500 border border-dashed rounded-lg bg-gray-50 p-5 text-center">
              {t`No requests observed yet. Activity starts after this control page is open.`}
            </p>
          ) : (
            <ol class="space-y-2 max-h-[calc(100vh-12rem)] overflow-y-auto pr-1">
              {[...events].reverse().map((event) => (
                <li
                  key={`${event.at}-${event.method}-${event.url}`}
                  class="rounded-lg border border-gray-200 p-3 text-sm"
                >
                  <div class="flex items-center justify-between gap-2">
                    <span class="font-mono font-semibold">{event.method}</span>
                    <div class="flex items-center gap-2">
                      <span
                        class={`rounded-full px-2 py-0.5 text-xs font-semibold ${actionStyle(event.action)}`}
                      >
                        {event.action === "status"
                          ? t`HTTP error`
                          : event.action === "hang"
                            ? t`No response`
                            : event.action === "delayed"
                              ? t`Delayed`
                              : t`Passed through`}
                      </span>
                      <time class="text-xs text-gray-500">
                        {new Date(event.at).toLocaleTimeString()}
                      </time>
                    </div>
                  </div>
                  <p class="mt-1 break-all font-mono text-xs text-gray-700">
                    {event.url}
                  </p>
                  <p class="mt-2 text-xs text-gray-500">
                    {event.status === undefined ? "" : `HTTP ${event.status}`}
                    {event.content === undefined
                      ? ""
                      : event.content === "taler-error"
                        ? t` · Taler JSON error`
                        : event.content === "empty"
                          ? t` · empty response body`
                          : " · text/html"}
                    {event.delayMs === undefined
                      ? ""
                      : t` · ${event.delayMs}ms delay`}
                    {event.failed ? t` · network failure` : ""}
                    {event.ruleId
                      ? t` · rule ${event.ruleId}`
                      : t` · default`}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </main>
  );
}
